import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Moon, Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PageHeader } from "@/components/PageHeader";
import { OvernightTag } from "@/components/ShiftChip";
import { api, errMsg, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PayPeriodSettings } from "@/pages/admin/PayPeriodSettings";
import { ShiftReminderSettings } from "@/pages/admin/ShiftReminderSettings";
import { StaffAppSettings } from "@/pages/admin/StaffAppSettings";

const BLANK = { name: "", colour: "#4F46E5", start_time: "09:00", end_time: "17:00", is_sleepover: false, ends_next_day: false, sleepover_allowance: 0, unpaid_sleep_hours: 0 };

const TypeForm = ({ t, onSaved }) => {
  const [f, setF] = useState(t);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    const body = { name: f.name, colour: f.colour, start_time: f.start_time, end_time: f.end_time, is_sleepover: f.is_sleepover, ends_next_day: f.end_time <= f.start_time || !!f.ends_next_day,
      sleepover_allowance: Number(f.sleepover_allowance), unpaid_sleep_hours: Number(f.unpaid_sleep_hours) };
    try {
      if (t.id) await api.put(`/biz/shift-types/${t.id}`, body);
      else await api.post("/biz/shift-types", body);
      toast.success("Shift type saved");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <form onSubmit={save} className="space-y-3">
      <div className="grid grid-cols-[1fr_80px] gap-3">
        <div className="space-y-1.5"><Label>Name</Label><Input required value={f.name} onChange={set("name")} data-testid="type-name-input" /></div>
        <div className="space-y-1.5"><Label>Colour</Label><Input type="color" value={f.colour} onChange={set("colour")} className="h-10 p-1" data-testid="type-colour-input" /></div>
        <div className="space-y-1.5"><Label>Start</Label><Input type="time" value={f.start_time} onChange={set("start_time")} data-testid="type-start-input" /></div>
        <div className="space-y-1.5"><Label>End</Label><Input type="time" value={f.end_time} onChange={set("end_time")} data-testid="type-end-input" /></div>
      </div>
      <label className="flex items-center justify-between rounded-lg border border-slate-200 p-3 text-sm font-medium">
        Finishes the next day (e.g. 10:00 → 10:30 next day)
        <Switch checked={f.end_time <= f.start_time || !!f.ends_next_day} disabled={f.end_time <= f.start_time} onCheckedChange={(v) => setF({ ...f, ends_next_day: v })} data-testid="type-next-day-switch" />
      </label>
      <label className="flex items-center justify-between rounded-lg bg-purple-50 p-3 text-sm font-medium text-purple-900">
        <span className="flex items-center gap-2"><Moon className="h-4 w-4" />Sleepover / on-call shift</span>
        <Switch checked={f.is_sleepover} onCheckedChange={(v) => setF({ ...f, is_sleepover: v })} data-testid="type-sleepover-switch" />
      </label>
      {f.is_sleepover && (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Sleepover allowance £</Label><Input type="number" step="0.01" min="0" value={f.sleepover_allowance} onChange={set("sleepover_allowance")} data-testid="type-allowance-input" /></div>
          <div className="space-y-1.5"><Label>Unpaid sleep hours</Label><Input type="number" step="0.25" min="0" max="24" value={f.unpaid_sleep_hours} onChange={set("unpaid_sleep_hours")} data-testid="type-unpaid-input" /></div>
        </div>
      )}
      <Button type="submit" className="w-full rounded-full" data-testid="type-save-btn">Save</Button>
    </form>
  );
};

const Locations = () => {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const load = useCallback(() => {
    api.get("/biz/locations").then((r) => setRows(r.data));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/biz/locations", { name });
      setName("");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6">
      <h2 className="font-display text-xl font-semibold">Locations</h2>
      <form onSubmit={add} className="mt-4 flex gap-2">
        <Input required placeholder="e.g. Ward B, Kitchen" value={name} onChange={(e) => setName(e.target.value)} data-testid="location-name-input" />
        <Button type="submit" data-testid="location-add-btn"><Plus className="h-4 w-4" /></Button>
      </form>
      <ul className="mt-4 divide-y divide-slate-100">
        {rows.map((l) => (
          <li key={l.id} className="flex items-center justify-between py-2 text-sm" data-testid={`location-row-${l.id}`}>
            {l.name}
            <Button size="icon" variant="ghost" onClick={() => window.confirm(`Delete ${l.name}?`) && api.delete(`/biz/locations/${l.id}`).then(load).catch((e) => toast.error(errMsg(e)))} data-testid={`location-delete-btn-${l.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
          </li>
        ))}
      </ul>
    </div>
  );
};

const BusinessName = () => {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.business?.name || "");
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/biz/settings", { name });
      toast.success("Saved");
      refresh();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <form onSubmit={save} className="rounded-xl border border-slate-200 bg-white p-6">
      <h2 className="font-display text-xl font-semibold">Business</h2>
      <div className="mt-4 flex gap-2">
        <Input required value={name} onChange={(e) => setName(e.target.value)} data-testid="business-name-input" />
        <Button type="submit" data-testid="business-name-save-btn">Save</Button>
      </div>
    </form>
  );
};

export default function SettingsPage() {
  const [types, setTypes] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => {
    api.get("/biz/shift-types").then((r) => setTypes(r.data));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const remove = async (id) => {
    if (!window.confirm("Delete this shift type?")) return;
    await api.delete(`/biz/shift-types/${id}`).catch((e) => toast.error(errMsg(e)));
    load();
  };

  return (
    <div>
      <PageHeader title="Settings" subtitle="Shift types, sleepovers and locations">
        <Button className="rounded-full" onClick={() => setEditing(BLANK)} data-testid="add-shift-type-btn"><Plus className="mr-1 h-4 w-4" />Shift type</Button>
      </PageHeader>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {types.map((t) => (
          <div key={t.id} className="rounded-xl border border-slate-200 bg-white p-6" style={{ borderTop: `4px solid ${t.colour}` }} data-testid={`shift-type-card-${t.id}`}>
            <div className="flex items-start justify-between">
              <p className="font-display text-lg font-semibold">{t.name}</p>
              <div>
                <Button size="icon" variant="ghost" onClick={() => setEditing(t)} data-testid={`shift-type-edit-btn-${t.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => remove(t.id)} data-testid={`shift-type-delete-btn-${t.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
              </div>
            </div>
            <p className="font-mono text-sm">{t.start_time}–{t.end_time}{(t.ends_next_day || t.end_time <= t.start_time) && <OvernightTag />}</p>
            {t.is_sleepover && <p className="mt-2 text-xs text-purple-700">Sleepover · {money(t.sleepover_allowance)} allowance · {t.unpaid_sleep_hours}h unpaid sleep</p>}
          </div>
        ))}
      </div>
      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <Locations />
        <BusinessName />
        <StaffAppSettings />
        <PayPeriodSettings />
        <ShiftReminderSettings />
      </div>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="shift-type-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit shift type" : "New shift type"}</DialogTitle></DialogHeader>
          {editing && <TypeForm t={editing} onSaved={() => { setEditing(null); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
